import { round } from '@kunalabs-io/kai'

export interface DotWithLabel {
  entry: ChartEntry
  x: number
  y: number
  color: string
  yAxisId: 'left' | 'right'
  value: string
  label: string
  position: 'top' | 'bottom' | 'left' | 'right'
  dx?: number
  dy?: number
}

export interface ChartEntry {
  timestampMs: number
  tvl: number | null
  apy: number | null
  label: string
}

export const formatNumber = (val: number): string => {
  return `$${round(val, 0).toLocaleString('en-US', { maximumFractionDigits: 2 })}`
}

interface ResolveDotsOptions {
  dots: DotWithLabel[]
  svgWidth: number
  svgHeight: number
  margin: { left: number; right: number; top?: number; bottom?: number }
  threshold?: number
  horizontalOffset?: number
  verticalOffset?: number
}

export const resolveOverlappingDots = ({
  dots,
  svgWidth,
  svgHeight,
  margin,
  threshold = 50,
  horizontalOffset = 32,
  verticalOffset = 16,
}: ResolveDotsOptions): DotWithLabel[] => {
  const resolved = [...dots]

  for (let i = 0; i < resolved.length; i++) {
    for (let j = i + 1; j < resolved.length; j++) {
      const a = resolved[i]
      const b = resolved[j]

      const xClose = Math.abs(a.x - b.x) < threshold
      const yClose = Math.abs(a.y - b.y) < threshold

      if (xClose && yClose) {
        const tooCloseToLeft = Math.min(a.x, b.x) - horizontalOffset < margin.left + 8
        const tooCloseToRight = Math.max(a.x, b.x) + horizontalOffset > svgWidth - margin.right - 8
        const tooCloseToBottom = Math.max(a.y, b.y) + verticalOffset > svgHeight - 8
        const tooCloseToTop = Math.min(a.y, b.y) - verticalOffset < (margin.top ?? 0) + 8

        const exactlySame = Math.abs(a.x - b.x) < 1 && Math.abs(a.y - b.y) < 1

        if (exactlySame) {
          resolved[i] = { ...a, dx: 0, dy: -verticalOffset / 2, position: 'top' }
          resolved[j] = { ...b, dx: 0, dy: verticalOffset / 2, position: 'bottom' }
        } else if (tooCloseToLeft || tooCloseToRight) {
          resolved[i] = { ...a, dx: 0, dy: -verticalOffset / 2, position: 'top' }
          resolved[j] = { ...b, dx: 0, dy: verticalOffset / 2, position: 'bottom' }
        } else if (tooCloseToBottom || tooCloseToTop) {
          resolved[i] = { ...a, dx: -horizontalOffset, dy: 0, position: 'left' }
          resolved[j] = { ...b, dx: horizontalOffset, dy: 0, position: 'right' }
        } else {
          if (a.x < b.x) {
            a.dx = -horizontalOffset
            b.dx = horizontalOffset
          } else {
            a.dx = horizontalOffset
            b.dx = -horizontalOffset
          }
        }
      }
    }
  }

  return resolved
}
